import { MedplumClient, RateLimitInfo } from "@medplum/core";

// Medplum's rate limiter renews on a rolling window and reports remaining quota via the
// `RateLimit` response header on every request, surfaced through `medplum.rateLimitStatus()`.
// Checking before each write — instead of only reacting to a 429 — is what avoids tripping the
// limit at all instead of just retrying after the fact.
const DEFAULT_MIN_REMAINING_UNITS = 5;

/**
 * Waits out the server's rate-limit window when quota is nearly exhausted, based on the most
 * recent `RateLimit` response header Medplum attached to a request on this client.
 *
 * @param medplum - Medplum client
 * @param minRemainingUnits - Quota threshold below which this waits for the window to renew
 */
export async function waitForRateLimitRenewalIfNeeded(
  medplum: MedplumClient,
  minRemainingUnits: number = DEFAULT_MIN_REMAINING_UNITS,
): Promise<void> {
  // rateLimitStatus() reads state already cached on this client from the last response's
  // RateLimit header — it does not itself make a network call.
  const nearlyExhausted = medplum
    .rateLimitStatus()
    .filter((limit: RateLimitInfo) => limit.remainingUnits <= minRemainingUnits);

  if (nearlyExhausted.length === 0) {
    return;
  }

  const secondsUntilReset = Math.max(
    ...nearlyExhausted.map((limit: RateLimitInfo) => limit.secondsUntilReset),
  );

  await new Promise((resolve) => setTimeout(resolve, secondsUntilReset * 1000));
}
